import { useEffect, useState } from "react";
import { Card, CardContent, Grid, Typography, Stack, Box, Divider } from "@mui/material";
import AutocompleteComponent from "../Shared/AutocompleteComponent";
import CountUpComponent from "../Shared/CountUpComponent";
import { PartnersTableDto, PartnersMultiFiltersType, PartnersFilterType, TreeNode } from "../../types/dataTypes";
import { initialFiltersPartners } from "../../utils/consts";
import CountryRegionAutoComplete from "../Shared/CountryRegionAutoComplete";
import { Info, Business } from "@mui/icons-material";

type PartnersFiltersProps = {
  data: PartnersTableDto[];
  setFilteredData: Function;
  setLoading: Function;
  setReload: Function;
  filters: PartnersFilterType;
  setFilters: Function;
  isDrawerOpen: boolean;
  setIsDrawerOpen: Function;
  treeNodes?: TreeNode[];
}

function PartnersFilters({ data, setFilteredData, setLoading, setReload, filters, setFilters }: PartnersFiltersProps) {
  const [numberOfEntries, setNumberOfnumberOfEntries] = useState(data.length);
  const [countrySelectectionisAnd, setCountrySelectionisAnd] = useState(true);
  const [nameSelectectionisAnd, setNameSelectionisAnd] = useState(true);
  const [fosSelectectionisAnd, setfosSelectectionisAnd] = useState(true);

  // State for available options for each filter
  const [availableOptions, setAvailableOptions] = useState<PartnersMultiFiltersType>({
    countries: [],
    fos: [],
    names: [],
  });

  const resetFilters = () => {
    setFilters(initialFiltersPartners);
    setReload(true);
  };

  // Calculate available options based on filters and load related data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const filteredData = filterData('dataset');
        setNumberOfnumberOfEntries(filteredData.length);

        const newAvailableOptions: PartnersMultiFiltersType = {
          countries: countrySelectectionisAnd ? Array.from(new Set(filteredData.map(item => item.countries).flat())).sort()
            : Array.from(new Set(filterData('options').map(item => item.countries).flat())).sort(),
          fos: fosSelectectionisAnd ? Array.from(new Set(filteredData.map(item => item.fos).flat())).sort()
            : Array.from(new Set(filterData('options').map(item => item.fos).flat())).sort(),
          names: Array.from(new Set(filteredData.map(item => item.name))).sort(),
        };
        setAvailableOptions(newAvailableOptions);

        setFilteredData(filteredData);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // See the identical comment in ReferenceFilters.tsx: `data` arrives asynchronously after
    // mount, so it must be a dependency here or counts/options never update once it loads.
  }, [filters, countrySelectectionisAnd, fosSelectectionisAnd, data]);


  //filter Items function give back a boolean value
  const filterData = (mode: 'dataset' | 'options'): PartnersTableDto[] => {
    const filteredData = data.filter(item => {
      const itemCountries = item.countries.flat();
      const itemCountriesUnique = Array.from(new Set(itemCountries));
      const itemFieldsOfServices = item.fos.flat();
      const itemFieldsOfServicesUnique = Array.from(new Set(itemFieldsOfServices));

      const namesCondition = filters.name.length === 0 ||
        (mode === 'dataset' ? nameSelectectionisAnd ?
          filters.name.some(n => item.name.includes(n)) :
          filters.name.some(n => item.name.includes(n)) : true);

      const fosCondition = filters.fos.length === 0 ||
        (mode === 'dataset' ? fosSelectectionisAnd ?
          filters.fos.some(service => itemFieldsOfServicesUnique.includes(service)) :
          filters.fos.some(service => itemFieldsOfServicesUnique.includes(service)) : true);

      const countryCondition = filters.countries.length === 0 ||
        (mode === 'dataset' ? countrySelectectionisAnd ?
          filters.countries.some(country => itemCountriesUnique.includes(country)) :
          filters.countries.some(country => itemCountriesUnique.includes(country)) : true);

      return fosCondition &&
        namesCondition &&
        countryCondition
        ;
    })
    return filteredData;
  };

  const handleNameChange = (event: any, newValue: any) => {
    setFilters((prevFilters: PartnersFilterType) => ({ ...prevFilters, name: newValue }));
  };

  const handleFieldOfServiceChange = (event: any, newValue: any) => {
    setFilters((prevFilters: PartnersFilterType) => ({ ...prevFilters, fos: newValue }));
  };

  return (
    <>
      <Grid container spacing={1} justifyContent="space-between" >
        <Grid item xs={12} md={10}>
          <Card sx={{ marginTop: 0, overflow: "visible" }} >
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} paddingBottom={2}>
                <Info color="action" />
                <Typography variant="h6">General</Typography>
              </Stack>
              <Grid container justifyContent="space-evenly">
                <Grid item xs={12} md={4}>
                  <AutocompleteComponent
                    options={availableOptions.names}
                    value={filters.name}
                    handleChange={handleNameChange}
                    isMulti={true}
                    label="Name"
                    radioState={nameSelectectionisAnd}
                    setRadioState={setNameSelectionisAnd}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <CountryRegionAutoComplete
                    availableOptions={availableOptions}
                    filters={filters}
                    countrySelectectionisAnd={countrySelectectionisAnd}
                    setCountrySelectionisAnd={setCountrySelectionisAnd}
                    setFilters={setFilters}
                  ></CountryRegionAutoComplete>
                </Grid>
              </Grid>
              <Divider style={{ color: 'lightgrey', marginTop: 20, marginBottom: 12 }} />
              <Box
                sx={{
                  marginBottom: 2,
                  borderRadius: 1,
                }}
              >
                <Stack direction="row" alignItems="center" gap={1}>
                  <Business color="action" />
                  <Typography variant="h6">
                    Expertise
                  </Typography>
                </Stack>
              </Box>
              <Grid container justifyContent="space-evenly">
                <Grid item xs={12} md={4}>
                  <AutocompleteComponent
                    options={availableOptions.fos}
                    value={filters.fos}
                    handleChange={handleFieldOfServiceChange}
                    isMulti={true}
                    label="Field of Service"
                    radioState={fosSelectectionisAnd}
                    setRadioState={setfosSelectectionisAnd}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={2}>
          <CountUpComponent resetFilters={resetFilters} end={numberOfEntries} />
        </Grid>
      </Grid>
    </>
  );
}

export default PartnersFilters;
